import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TitleContext from '../../../TitleContext'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import { useLocation, useNavigate } from 'react-router'
import {
  EnrollmentDiscount,
  EnrollmentFee,
  EnrollmentFeeStatusEnum,
  FetchBillingResponse,
  LicensingBill,
  OrderByDirection,
  PaginationResponse,
  PaymentConfiguration,
  PaymentConfigurationCodeEnum,
  Role,
  UserAccountDetails,
} from '../../../swagger'
import UserAccountCard from '../../Card/UserAccountCard'
import { fullName } from '../../../utils/fullName'
import { meta, paymentsApi, userAccountsApi } from '../../../api/swagger'
import EmptyUserAccount from './EmptyUserAccount'
import RolesTable from '../Roles/RolesTable'
import { UserRoleAssignment } from '../../../swagger'
import { Page } from '../../Elements/PageMargins'
import { Payment, PaymentMade } from '../../Interfaces/Payment'
import Card from '@mui/material/Card'
import BillingHistorySummaryTable, {
  BillingHistorySummaryTableVariant,
} from '../../Account/Billing/BillingHistorySummaryTable'
import { useAuth } from '../../Routes/AuthProvider'
import { Can } from '@casl/react'
import ProgramOption from '../../Interfaces/ProgramOption'
import getLocaleCurrencyForAmount from '../../../utils/getLocaleCurrencyForAmount'
import { dateToDashString, IS_UTC } from '../../../utils/dateUtility'
import { styled } from '@mui/system'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import useLoadingContext from '../../../hooks/useLoadingContext'
import getOrderByClause from '../../../utils/getOrderByClause'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import AccountingViewCountryCoordinatorLicensingTable from '../../Account/Billing/AccountingViewCountryCoordinatorLicensingTable'
import { CountryCoordinatorLicensingPaymentHistory } from '../../Account/Billing/CountryCoordinatorLicensingPaymentHistory'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import ActorBillingView from '../../Account/Billing/DefaultBillingView/ActorBillingView'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
  getBigZero,
} from '../../../utils/bigJSHelper'

export const countryCoordinatorRoleName = 'country coordinator'

const TransactionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    // Don't let the Enrollment Payment Transactions table get overlaid by buttons on mobile
    marginBottom: theme.spacing(12),
  },
}))

export interface LicensingProgramOptionsWithOwedValue {
  name: string
  programKey: number
  studentKey: number | undefined
  actorKey?: number
}
interface UserAccountProps {
  userId: string
}

export const UserAccount: React.FunctionComponent<UserAccountProps> = (
  props
) => {
  const { userId } = props
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useAuth()
  const { permissionAbility } = useAuth()
  const location = useLocation()

  const { useTitleEffect } = React.useContext(TitleContext)
  const title = t('UserAccount.User.Title', 'User Accounts')
  useTitleEffect(title)

  const { handleError } = useAppSnackbar()

  const {
    UserAccounts: {
      fetchUser: fetchUserLoadingId,
      fetchUsers: fetchUsersLoadingId,
      fetchUserAccountRoles: fetchUserAccountRolesLoadingId,
      fetchBilling: fetchBillingLoadingId,
      fetchPaymentOptions: fetchPaymentOptionsLoadingId,
    },
    Meta: { fetchRoles: fetchRolesMetaLoadingId },
  } = useLoadingIds()

  const fetchRolesLoadingId = `${fetchRolesMetaLoadingId}-UserAccount`

  const [userAccount, setUserAccount] = useState<UserAccountDetails>()
  const [roles, setRoles] = useState<UserRoleAssignment[]>([])

  const errorMessage = t('UserAccount.Error', 'Something went wrong.')

  const [isLoading, setIsLoading] = useState(true)

  const [roleOptions, setRoleOptions] = useState<Role[]>([])

  const [pagination, setPagination] = useState<PaginationResponse>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    orderBy: [{ name: OrderByDirection.Asc }],
  })

  const fetchUser = async () => {
    try {
      const fetchedUserAccount = await userAccountsApi.fetchUser({
        userKey: parseInt(userId),
      })
      setUserAccount(fetchedUserAccount)
      setNameForBreadCrumb(
        fullName({
          firstName: fetchedUserAccount?.firstName,
          lastName: fetchedUserAccount?.lastName,
        })
      )
    } catch (error) {
      await handleError(error, errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const { addLoadingIds } = useLoadingContext({
    asyncFunction: fetchUser,
    loadingId: fetchUserLoadingId,
  })

  useEffect(() => {
    addLoadingIds([
      fetchUserLoadingId,
      fetchRolesLoadingId,
      fetchUserAccountRolesLoadingId,
    ])

    if (auth.permissionAbility.can('editRoleAssignment', 'User')) {
      addLoadingIds([fetchUsersLoadingId])
    }
  }, [
    auth.permissionAbility,
    location.pathname,
    addLoadingIds,
    fetchUserLoadingId,
    fetchRolesLoadingId,
    fetchUserAccountRolesLoadingId,
    fetchUsersLoadingId,
  ])

  const fetchPaginatedRoles = async () => {
    try {
      const { roles, pagination: paginationResponse } =
        await userAccountsApi.fetchUserAccountRoles({
          userKey: parseInt(userId),
          page: pagination.page,
          pageSize: pagination.pageSize,
          orderBy: getOrderByClause(pagination.orderBy),
        })

      if (roles) {
        setRoles(roles)
      }
      if (paginationResponse) {
        const { page, pageSize, totalCount } = paginationResponse

        setPagination({
          ...pagination,
          page,
          pageSize,
          totalCount,
        })
      }
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const { triggerFetch } = useLoadingContext({
    asyncFunction: fetchPaginatedRoles,
    loadingId: fetchUserAccountRolesLoadingId,
  })

  useEffect(() => {
    triggerFetch()
  }, [pagination.page, pagination.pageSize, pagination.orderBy, triggerFetch])

  const triggerRefresh = () => {
    fetchUser()
    fetchPaginatedRoles()
  }

  const failedFetchingRolesMessage = t(
    'UserAccounts.Error.FetchRoles',
    'An unknown error occurred fetching roles.'
  )

  const fetchRoles = async () => {
    const abortController = new AbortController()
    try {
      const _roles = await meta.fetchRoles()
      if (!abortController.signal.aborted) {
        setRoleOptions(
          _roles.sort((a, b) =>
            new Intl.Collator(i18n.language).compare(a.name, b.name)
          )
        )
      }
    } catch (error) {
      await handleError(error, failedFetchingRolesMessage)
    } finally {
      abortController.abort()
    }
  }

  useLoadingContext({
    asyncFunction: fetchRoles,
    loadingId: fetchRolesLoadingId,
  })

  const generateKeyForFeeMaps = (program: number, studentKey?: number) => {
    if (!!studentKey) {
      return `${studentKey}-${program}`
    }
    return `${program}`
  }

  const generateKeyForEnrollmentProgramOptionsMap = (
    program: number,
    studentKey: number
  ): string => {
    return `${program}-${studentKey}`
  }

  const [billingResponse, setBillingResponse] = useState(
    {} as FetchBillingResponse
  )

  const [configurations, setConfigurations] = useState<PaymentConfiguration[]>(
    []
  )

  const fetchBilling = async () => {
    const fetchedBilling = await paymentsApi.fetchBilling({
      userKey: parseInt(userId),
    })
    const { configurations: fetchedConfigs } = fetchedBilling
    setConfigurations(fetchedConfigs)

    setBillingResponse(fetchedBilling)
  }

  const { triggerFetch: triggerFetchBilling } = useLoadingContext({
    asyncFunction: fetchBilling,
    loadingId: fetchBillingLoadingId,
  })

  // Fetch Billing
  useEffect(() => {
    if (permissionAbility.can('viewBilling', 'Payment')) {
      triggerFetchBilling()
    }
  }, [userId, permissionAbility, triggerFetchBilling])

  const achDiscount =
    configurations.find(
      (configuration) =>
        configuration.code === PaymentConfigurationCodeEnum.AchDiscount
    )?.amount ?? '0.00'

  const [licensingProgramOptions, setLicensingProgramOptions] =
    useState<ProgramOption[]>()

  const [enrollmentProgramOptions, setEnrollmentProgramOptions] =
    useState<ProgramOption[]>()

  // A user must be able to edit licensing and enrollment payments and view programs and payments before being allowed to fetchPaymentOptions
  const hasPermissionToFetchPaymentOptions =
    auth.permissionAbility.can('updateEnrollmentPayment', 'Payment') &&
    auth.permissionAbility.can('updateLicensingPayment', 'Payment') &&
    auth.permissionAbility.can('createEnrollmentPayment', 'Payment') &&
    auth.permissionAbility.can('createLicensingPayment', 'Payment') &&
    auth.permissionAbility.can('view', 'Program') &&
    auth.permissionAbility.can('view', 'Payment')

  const hasPermissionToEditLicensingPaymentsOnlyDownline =
    auth.permissionAbility.can('updateLicensingPaymentOnlyDownline', 'Payment')

  useEffect(() => {
    if (
      !licensingProgramOptions &&
      !enrollmentProgramOptions &&
      (hasPermissionToFetchPaymentOptions ||
        hasPermissionToEditLicensingPaymentsOnlyDownline)
    ) {
      addLoadingIds([fetchPaymentOptionsLoadingId])
    }
  }, [
    licensingProgramOptions,
    enrollmentProgramOptions,
    hasPermissionToFetchPaymentOptions,
    addLoadingIds,
    fetchPaymentOptionsLoadingId,
    hasPermissionToEditLicensingPaymentsOnlyDownline,
  ])

  const fetchPaymentOptions = async () => {
    try {
      const { programs, enrollments } = await paymentsApi.fetchPaymentOptions({
        userKey: parseInt(userId),
      })

      setLicensingProgramOptions(
        programs.map(
          ({
            programType,
            semesterOneStartDate,
            communityName,
            programKey,
            actorKey,
          }) => {
            const name = `${programType} (${dateToDashString(
              semesterOneStartDate,
              IS_UTC
            )}) - ${communityName}`

            const programOption: ProgramOption = {
              name,
              programKey,
              actorKey,
            }

            return programOption
          }
        )
      )
      const enrollmentMap = new Map<
        string /** `programKey-studentKey`*/,
        string /** programType, studentName, semesterOneStartDate */
      >()
      setEnrollmentProgramOptions(
        enrollments.map((enrollment) => {
          const name = `${enrollment.studentFirstName} - ${
            enrollment.programType
          } (${dateToDashString(enrollment.semesterOneStartDate, IS_UTC)})`
          const programOption: ProgramOption = {
            name,
            programKey: enrollment.programKey,
            studentKey: enrollment.studentKey,
          }
          const key = generateKeyForEnrollmentProgramOptionsMap(
            enrollment.programKey,
            enrollment.studentKey
          )
          enrollmentMap.set(key, name)
          return programOption
        })
      )
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  useLoadingContext({
    asyncFunction: fetchPaymentOptions,
    loadingId: fetchPaymentOptionsLoadingId,
  })

  const enrollmentPaymentHistory =
    billingResponse.enrollment?.history?.map((it) => {
      const paymentHistory: Payment = {
        ...it,
        paymentsMade: it.enrollmentPayments.map((ep) => {
          return {
            paymentKey: it.paymentKey,
            studentKey: ep.studentKey,
            programKey: ep.programKey,
          } as PaymentMade
        }),
      }
      return paymentHistory
    }) ?? []

  const enrollmentTotalDue =
    billingResponse.enrollment?.owed?.total.amount ?? '0.00'

  const licensingTotalDue = formatBigFixedTwoDecimals(
    billingResponse.licensing?.programs
      ?.map((licensingBill) => licensingBill.total)
      .map((licensingBill) => licensingBill.amount)
      .reduce(
        (licensingBillAccumulator, licensingBill) =>
          licensingBillAccumulator.plus(licensingBill),
        getBigZero()
      ) ?? getBigZero()
  )

  const enrollmentFeeMap = new Map<string, EnrollmentFee>()
  const licensingFeeMap = new Map<string, LicensingBill>()
  const discountMap = new Map<string, EnrollmentDiscount>()

  billingResponse.enrollment?.owed?.fees.forEach((fee) => {
    if (!!fee.total) {
      enrollmentFeeMap.set(
        generateKeyForFeeMaps(fee.programKey, fee.studentKey),
        fee
      )
    }
  })

  billingResponse.licensing?.programs?.forEach((item) => {
    if (!!item.fees) {
      licensingFeeMap.set(generateKeyForFeeMaps(item._for.programKey), item)
    }
  })

  billingResponse.enrollment?.owed?.discounts?.forEach((discount) => {
    discountMap.set(
      generateKeyForFeeMaps(discount.programKey ?? 0, discount.studentKey ?? 0),
      discount
    )
  })

  const enrollmentProgramOptionsWithOwedValue = enrollmentProgramOptions?.map(
    (enrollment) => {
      const key = generateKeyForFeeMaps(
        enrollment.programKey,
        enrollment.studentKey
      )
      const total = enrollmentFeeMap.get(key)
      let cashAmount = 'N/A'

      const discount = discountMap.get(key)

      const totalWithDiscount = formatBigFixedTwoDecimals(
        convertTwoDecimalStringToBig(total?.total.amount ?? '0.00').minus(
          discount?.total.amount ?? '0.00'
        )
      )

      if (total) {
        cashAmount =
          total.status === EnrollmentFeeStatusEnum.Unpaid
            ? getLocaleCurrencyForAmount(
                totalWithDiscount,
                total?.total.currencyCode
              )
            : EnrollmentFeeStatusEnum.Paid
      }

      return {
        name: `${enrollment.name} ${cashAmount}`,
        programKey: enrollment.programKey,
        studentKey: enrollment.studentKey,
      }
    }
  )

  const licensingProgramOptionsWithOwedValue:
    | LicensingProgramOptionsWithOwedValue[]
    | undefined = licensingProgramOptions?.map((licensing) => {
    const key = generateKeyForFeeMaps(licensing.programKey)
    const total = licensingFeeMap.get(key)
    const fees = total?.total
    let cashAmount = 'N/A'

    if (!!fees) {
      cashAmount = getLocaleCurrencyForAmount(fees.amount, fees.currencyCode)
    }
    return {
      name: `${licensing.name} ${cashAmount}`,
      programKey: licensing.programKey,
      studentKey: licensing.studentKey,
      actorKey: licensing.actorKey,
    }
  })

  const hasCountryCoordinatorRoles = roles.some(
    (role) => role.name.toLowerCase() === countryCoordinatorRoleName
  )

  const canViewCountryCoordinatorLicensingTable = permissionAbility.can(
    'internationalLicensingView',
    'Payment'
  )

  const countryCoordinatorFilteredRoles = roles.filter(
    (role) => role.name.toLowerCase() === countryCoordinatorRoleName
  )

  const [nameForBreadcrumb, setNameForBreadCrumb] = useState('')

  const updateName = (fullName: string) => {
    setNameForBreadCrumb(fullName)
  }

  const navigateToUserAccounts = () => {
    navigate(
      {
        pathname: '/admin/user-accounts',
      },
      {
        /** Navigation Options */
      }
    )
  }

  if (!userAccount) {
    return <EmptyUserAccount isLoading={isLoading} />
  }

  const handleUserRolesPagination = (pageInformation: PaginationResponse) => {
    if (pagination.page < 1) return

    setPagination({
      ...pageInformation,
      orderBy: pageInformation.orderBy,
      page: pageInformation.page + 1,
    })
  }

  const showAccordions =
    !!billingResponse.licensing?.programs &&
    billingResponse.licensing.programs?.length > 1

  return (
    <Page withinTab>
      {!!userAccount && (
        <>
          <DynamicBreadcrumbs
            breadcrumbs={[
              {
                label: title,
                onClick: navigateToUserAccounts,
              },
              {
                label: nameForBreadcrumb,
              },
            ]}
          />
          <UserAccountCard
            initialUserAccountDetails={userAccount}
            updateNameForParent={updateName}
            {...props}
          />
          <RolesTable
            roles={roles}
            userKey={parseInt(userId)}
            userAccount={userAccount}
            removeRole={fetchUser}
            triggerRefresh={triggerRefresh}
            {...props}
            roleOptions={roleOptions}
            handleUserRolesPagination={handleUserRolesPagination}
            pagination={pagination}
          />
          <Can I={'viewBilling'} on="Payment" ability={permissionAbility}>
            <TransactionCard>
              <BillingHistorySummaryTable
                enrollmentProgramOptions={enrollmentProgramOptionsWithOwedValue}
                variant={
                  BillingHistorySummaryTableVariant.EnrollmentPaymentsCSR
                }
                ariaLabelledBy="enrollmentPaymentTransactionsTableHeader"
                paymentHistory={enrollmentPaymentHistory}
                amountDue={enrollmentTotalDue}
                userKey={parseInt(userId)}
                triggerRefresh={triggerFetchBilling}
                hasPermissionToFetchPaymentOptions={
                  hasPermissionToFetchPaymentOptions
                }
                {...props}
              />
            </TransactionCard>
          </Can>

          {hasCountryCoordinatorRoles &&
          canViewCountryCoordinatorLicensingTable ? (
            <>
              <TransactionCard>
                <AccountingViewCountryCoordinatorLicensingTable
                  roles={countryCoordinatorFilteredRoles}
                />
              </TransactionCard>

              <TransactionCard>
                <CountryCoordinatorLicensingPaymentHistory
                  userKey={Number(userId)}
                />
              </TransactionCard>
            </>
          ) : (
            <Can I={'viewBilling'} on="Payment" ability={permissionAbility}>
              <ActorBillingView
                billingData={billingResponse}
                achDiscount={achDiscount ?? '0.00'}
                amountDue={licensingTotalDue}
                hasPermissionToFetchPaymentOptions={
                  hasPermissionToFetchPaymentOptions
                }
                licensingProgramOptions={licensingProgramOptionsWithOwedValue}
                triggerRefetch={triggerFetchBilling}
                variantForBillingHistorySummaryTable={
                  BillingHistorySummaryTableVariant.LicensingPaymentsCSR
                }
                showEmptyPage={false}
                showAccordions={showAccordions}
                userKey={parseInt(userId)}
                isTableHeaderVisible={false}
                userRoles={userAccount?.roles}
              />
            </Can>
          )}
        </>
      )}
    </Page>
  )
}

export default UserAccount
